<template>
  <div class="menu--item" :data-menu-item-id="item.mi_id" :class="containerClasses"
       v-if="isScheduled || $store.state.isAdminView" :id="'menu--item-' + item.mi_id">
    <template v-if="'' === tagLocation">
      <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="visual"></menu-tag>
    </template>
    <span v-if="showBin" :aria-label="'bin ' + getItemBIN(item)" class="menu--item_bin">
      <span aria-hidden="true">{{ getItemBIN(item) }}</span>
    </span>
    <h3 class="menu--item_title">
      <template v-if="'before-title' === tagLocation">
        <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="visual"></menu-tag>
      </template>
      <span v-html="(showBin) ? removeBINFromTitle(item) : item.mi_title"></span>
      <template v-if="'after-title' === tagLocation">
        <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="visual"></menu-tag>
      </template>
      <span class="menu--item_subtitle" v-if="item.mi_subtitle.length" v-html="item.mi_subtitle"></span>
      <menu-item-price v-if="layout === 'price-inline' && item.price_display.length && !pricingTypes.length"
                       :price-to-display="priceToDisplay" :price-label="item.price_label"></menu-item-price>
    </h3>

    <menu-schedules v-if="$store.state.isAdminView && item.normalizedSchedules.length"
                    :normalized-schedules="item.normalizedSchedules"></menu-schedules>

    <div v-if="item.mi_description.length" class="menu--item_description" v-html="item.mi_description"></div>

    <template v-if="Object.keys(pricingTypesToDisplay).length">
      <menu-item-price v-for="(pricingTypeData, pricingType) in pricingTypesToDisplay" :key="pricingType"
                       :price-to-display="pricingTypeData.price" :aria-prefix="pricingType"
                       :price-label="pricingTypeData.label || ''"></menu-item-price>
    </template>
    <menu-item-price v-else-if="layout !== 'price-inline' && priceToDisplay.length && !isGroupedByPrice"
                     :price-to-display="priceToDisplay" :price-label="item.price_label"></menu-item-price>
  </div>
</template>

<script lang="ts">
/**
 * Custom component for default menu item display.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.5.1
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2023-04-21 (1.0.0) (jhoward)
 *      - <biminiFoodItem> copied to <default-item>.
 *
 *    2023-06-08 (1.1.0) (jhoward)
 *      - Add `layout` prop to <default-item>.
 *      - Support "price-inline" layout in <default-item>.
 *
 *    2023-06-21 (1.1.1) (jhoward)
 *      - Support "price-below" layout in <default-item>.
 *
 *    2023-07-26 (1.2.0) (jhoward)
 *      - Rename <default> to <default-item>.
 *
 *    2023-07-26 (1.3.0) (jhoward)
 *      - Support schedules in <default-item>.
 *
 *    2023-09-08 (1.3.1) (jhoward)
 *      - Move <menu-tag> under the item title in <default-item>.
 *      - CSS tweaks in <default-item>.
 *
 *    2023-09-13 (1.3.2) (jhoward)
 *      - Add hideDenomination prop to <default-item>.
 *
 *    2023-09-13 (1.3.3) (jhoward)
 *      - Add isGroupedByPrice prop to <default-item>.
 *
 *    2023-10-05 (1.3.4) (jhoward)
 *      - Add .menu--item-multi_line_price class logic to <default-item>.
 *      - Add tagLocation prop to <default-item>.
 *
 *    2023-12-20 (1.3.5) (jhoward)
 *      - Support "before-title" tagLocation prop value in <default-item>.
 *
 *    2024-07-19 (1.3.6) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <default-item>.
 *      - Refactor VisualMenuDefaultItemComponent interface from <default-item> to menu defs file.
 *
 *    2025-02-11 (1.4.0) (jhoward)
 *      - Add <menu-schedules> to the <defaultVisualItem> component if in admin view.
 *
 *    2025-04-08 (1.5.0) (jhoward)
 *      - Rename <defaultVisualItem> to <defaultItem>.
 *      - Remove "visual_" prefix from class names in <defaultItem>.
 *      - Use <menu-item-price> in <defaultItem>.
 *      - Add <defaultItem>::pricingTypesToDisplay computed property.
 *      - Add <defaultItem>::getPriceLabelByPricingType() method.
 *
 *  2025-10-13 (1.5.1) (jhoward)
 *     - Change menu/item/defaultItem.vue to use pricingTypesToDisplay object length instead of pricingTypes array length to determine price display template visibility.
 *     - Update menu/item/defaultItem.vue pricingTypesToDisplay() method to return an empty object if no pricing types have a price, preventing display of empty price blocks.
 *     - Add CSS rules in menu/item/defaultItem.vue to increase vertical spacing between consecutive elements with class .menu--tag.
 */

import MenuTag from "../menu-tag.vue";
import {defineComponent, PropType} from "vue";
import {MENU_ITEM_LAYOUT, MENU_ITEM_TAG_LOCATIONS, MenuDefaultItemComponent, MenuItem} from "../defs";
import {
  computedIsScheduled,
  methodGetItemBIN,
  getPricePerType,
  methodRemoveBINFromTitle
} from "../../../helpers/menu-sections";
import MenuSchedules from "../schedule/MenuSchedules.vue";
import MenuItemPrice from "./MenuItemPrice.vue";

export default defineComponent({
  name: 'defaultItem',
  components: {
    MenuItemPrice,
    MenuSchedules,
    MenuTag
  },
  props: {
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
    showBin: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String as PropType<MENU_ITEM_LAYOUT>,
      default: ''
    },
    pricingTypes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    hideDenomination: {
      type: Boolean,
      default: false
    },
    isGroupedByPrice: {
      type: Boolean,
      default: false
    },
    tagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
  },
  computed: {
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--item-highlighted': this.item.mi_highlight === 1,
        'menu--item-no_price': !this.priceToDisplay.length,
        'menu--item-no_description': !this.item.mi_description.length,
        'menu--item-price_inline': 'price-inline' === this.layout,
        'menu--item-price_header': !!this.pricingTypes.length,
        'menu--item-multi_line_price': !!this.item.mi_multi_line_price,
      };
    },
    isScheduled: computedIsScheduled,
    priceToDisplay(): string {
      if (this.hideDenomination) {
        return this.item.price_display.replace(/\$/g, '');
      }

      return this.item.price_display;
    },
    pricingTypesToDisplay() {
      const output: { [key: string]: { price: string | number, label: string | null } } = {};
      let pricingTypeMatchCount = 0;
      this.pricingTypes.forEach(pricingType => {
        const price = getPricePerType(this.item.price_display, pricingType, '', this.hideDenomination);

        if (('' + price).length) {
          pricingTypeMatchCount++;
        }

        output[pricingType] = {
          price: price,
          label: this.getPriceLabelByPricingType(pricingType, price)
        };
      });

      if (0 === pricingTypeMatchCount) {
        return {};
      }

      return output;
    }
  },
  methods: {
    getItemBIN: methodGetItemBIN,
    removeBINFromTitle: methodRemoveBINFromTitle,


    getPriceLabelByPricingType(pricingType: string, price: string | number): string | null {
      if ('' + price === '') {
        return null;
      }

      return '$' + price + ' per ' + pricingType;
    },
  }
})
</script>

<style scoped>
.menu--item:not(.menu--item-multi_line_price) {
  position: relative;
}

.menu--item {
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu--item.menu--item-search_highlighted {
  background-color: #f7f3eb !important;
}

/*.menu--item:not(.menu--item-multi_line_price) {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 0.2fr;
  grid-template-rows: min-content min-content;
  grid-gap: 0.25em;
}*/

html .menu--item.menu--item-multi_line_price {
  display: block;
}

.menu--item.menu--item-multi_line_price:deep(.menu--item_price) {
  text-align: right;
}

/*.textual_menu--container .menu--item:not(.menu--item-multi_line_price) {
  grid-template-columns: 1fr 0.4fr;
}*/

.menu--item.menu--item-no_price {
  grid-template-columns: 1fr;
}

.menu--item.menu--item-no_description {
  grid-template-rows: 1fr;
}

.menu--item_title,
.menu--item.menu--item-multi_line_price:deep(.menu--item_title) {
  position: relative;
  font-size: 1em;
  font-weight: bold;
}

.textual_menu--section .menu--item_title,
.textual_menu--section .menu--item.menu--item-multi_line_price:deep(.menu--item_title) {
  font-size: 1.2em;
}

.menu--item_description {
  grid-row: 2;
  font-weight: normal;
  margin-top: -0.3em;
}

.visual_menu--container .menu--item_description {
  font-size: 0.8em;
}

.menu--tag {
  position: absolute;
  right: 100%;
  top: 0.2em;
}

.menu--tag + .menu--tag {
  margin-top: 1.25em;
}

.menu--tag + .menu--tag + .menu--tag {
  margin-top: 2.5em;
}

.menu--item-price.menu--item-price_inline {
  grid-template-columns: 1fr;
}
</style>